import {escape} from "lodash-es";
import type {CharMapping, PageSpan, PageTextIndex, ResolvedQuoteMatch, SpanMatch, StrippedCache} from "./types";

/** Per-character normalization: canonicalize variant families, lowercase */
export function normalizeChar(ch: string): string {
  switch (ch) {
    // Zero-width characters + soft hyphen -> remove
    case "\u200B":
    case "\u200C":
    case "\u200D":
    case "\uFEFF":
    case "\u00AD":
      return "";

    // Smart single quotes -> straight
    case "\u2018":
    case "\u2019":
    case "\u201A":
    case "\u201B":
      return "'";

    // Smart double quotes -> straight
    case "\u201C":
    case "\u201D":
    case "\u201E":
    case "\u201F":
      return '"';

    // Dashes -> hyphen
    case "\u2013":
    case "\u2014":
    case "\u2012":
    case "\u2015":
    case "\u2212":
      return "-";

    // Ellipsis -> three dots
    case "\u2026":
      return "...";

    // Ligatures
    case "\uFB00":
      return "ff";
    case "\uFB01":
      return "fi";
    case "\uFB02":
      return "fl";
    case "\uFB03":
      return "ffi";
    case "\uFB04":
      return "ffl";

    default:
      if (/\s/.test(ch)) return " ";
      return ch.toLowerCase();
  }
}

/** Collapse whitespace, normalize characters, lowercase */
export function normalizeWhitespace(text: string): string {
  const result: string[] = [];
  let lastWasSpace = true; // trim leading

  for (let i = 0; i < text.length; i++) {
    const expanded = normalizeChar(text[i]);
    if (expanded === "") continue;

    for (const c of expanded) {
      if (c === " ") {
        if (!lastWasSpace) {
          result.push(" ");
          lastWasSpace = true;
        }
      } else {
        result.push(c);
        lastWasSpace = false;
      }
    }
  }

  // Trim trailing space
  if (result.length > 0 && result[result.length - 1] === " ") {
    result.pop();
  }

  return result.join("");
}

/**
 * Strip non-alphanumeric characters and collapse the whitespace they leave
 * behind, returning the stripped string and a mapping from stripped positions
 * back to original positions. Collapsing (and trimming) mirrors
 * normalizeWhitespace so a spaced "a ... b" and an unspaced "a... b" both
 * reduce to "a b" and match each other.
 */
export function stripPunctuation(text: string): {stripped: string; toOriginal: number[]} {
  const chars: string[] = [];
  const toOriginal: number[] = [];
  let lastWasSpace = true; // trim leading whitespace

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === " ") {
      if (lastWasSpace) continue; // collapse runs left by stripped punctuation
      lastWasSpace = true;
    } else if (/[a-z0-9]/i.test(ch)) {
      lastWasSpace = false;
    } else {
      continue; // drop punctuation
    }
    chars.push(ch);
    toOriginal.push(i);
  }

  // Trim trailing whitespace
  if (chars[chars.length - 1] === " ") {
    chars.pop();
    toOriginal.pop();
  }

  return {stripped: chars.join(""), toOriginal};
}

/**
 * Remove a single leading list marker — an ordered-list number ("1.", "2)") or
 * a bullet ("*", "-", "•") — and the whitespace after it. A list-item extract
 * can carry a marker the rendered text omits, so dropping it lets the item
 * match. A bare digit run without a "." or ")" (e.g. "1.5", "5 things") is left
 * alone. Returns the input unchanged when it has no leading marker.
 */
export function stripLeadingListMarker(text: string): string {
  return text.replace(/^(?:\d+[.)]|[*\-•])\s+/, "");
}

/**
 * Generic normalization: iterate chars -> normalizeChar -> handle multi-char
 * expansion -> collapse spaces -> trim. Returns the normalized characters and
 * a parallel array of mappings of the same generic type as the input.
 */
export function normalizeWithMapping<T>(
  rawChars: string[],
  rawMappings: T[],
): {normalizedChars: string[]; mappings: T[]} {
  const normalizedChars: string[] = [];
  const mappings: T[] = [];
  let lastWasSpace = true; // trim leading

  for (let i = 0; i < rawChars.length; i++) {
    const expanded = normalizeChar(rawChars[i]);
    if (expanded === "") continue;

    for (const c of expanded) {
      if (c === " ") {
        if (!lastWasSpace) {
          normalizedChars.push(" ");
          mappings.push(rawMappings[i]);
          lastWasSpace = true;
        }
      } else {
        normalizedChars.push(c);
        mappings.push(rawMappings[i]);
        lastWasSpace = false;
      }
    }
  }

  // Trim trailing space
  if (normalizedChars.length > 0 && normalizedChars[normalizedChars.length - 1] === " ") {
    normalizedChars.pop();
    mappings.pop();
  }

  return {normalizedChars, mappings};
}

/**
 * Two-tier search on already-normalized text.
 * Tier 1: exact indexOf after normalizing the needle.
 * Tier 2: strip punctuation from both sides, indexOf, map positions back.
 * When both tiers miss, the needle is retried once with a leading list marker
 * removed, since a list-item extract can carry a marker ("1.", "* ") the render
 * drops. The retry runs only after a total miss, so it cannot change a match
 * the original needle already found.
 * Returns positions in `haystack`. Accepts/returns an optional stripped cache.
 */
export function findNormalized(
  haystack: string,
  needle: string,
  haystackCache?: StrippedCache,
): {start: number; end: number; haystackCache?: StrippedCache} | null {
  const normalizedNeedle = normalizeWhitespace(needle);
  const candidates = [normalizedNeedle];
  const deListed = stripLeadingListMarker(normalizedNeedle);
  if (deListed && deListed !== normalizedNeedle) candidates.push(deListed);

  let cache = haystackCache;
  for (const candidate of candidates) {
    // Tier 1: exact normalized match
    const idx = haystack.indexOf(candidate);
    if (idx !== -1) {
      return {start: idx, end: idx + candidate.length, haystackCache: cache};
    }

    // Tier 2: punctuation-stripped fallback
    cache = cache ?? stripPunctuation(haystack);
    const strippedNeedle = stripPunctuation(candidate);
    if (strippedNeedle.stripped.length === 0) continue;

    const strippedIdx = cache.stripped.indexOf(strippedNeedle.stripped);
    if (strippedIdx === -1) continue;

    // Map back: stripped position -> normalizedText position
    const start = cache.toOriginal[strippedIdx];
    const end = cache.toOriginal[strippedIdx + strippedNeedle.stripped.length - 1] + 1;
    return {start, end, haystackCache: cache};
  }

  return null;
}

/** JS (UTF-16) string index of the character beginning at UTF-8 byte
 * `byteOffset` in `s`. Extract offsets are UTF-8 byte offsets (the space the
 * server addresses), but `content` here is a JS string, so the two only
 * coincide on ASCII — this bridges them. Clamps to the string's bounds. */
export function charIndexOfByteOffset(s: string, byteOffset: number): number {
  if (byteOffset <= 0) return 0;
  const bytes = new TextEncoder().encode(s);
  const clamped = Math.min(byteOffset, bytes.length);
  return new TextDecoder().decode(bytes.subarray(0, clamped)).length;
}

/** Which occurrence of `text` (0-based) the UTF-8 byte `offset` names within
 * `content` — i.e. how many verbatim occurrences of `text` begin before it.
 * `text` is stored verbatim from `content`, so this counts raw (non-normalized)
 * non-overlapping matches; the answer disambiguates a quote that repeats. */
export function occurrenceInContent(content: string, text: string, offset: number): number {
  if (text.length === 0) return 0;
  const charOffset = charIndexOfByteOffset(content, offset);
  let occurrence = 0;
  for (let from = 0; ;) {
    const i = content.indexOf(text, from);
    if (i === -1 || i >= charOffset) break;
    occurrence++;
    from = i + text.length;
  }
  return occurrence;
}

/** The `n`-th (0-based) normalized occurrence of `needle` in an already-
 * normalized `haystack`. Falls back to the first occurrence when `haystack`
 * holds fewer than `n + 1` of them (the render and the source markdown disagree
 * on the count — e.g. table syntax or list markers add matches on one side).
 * When exact-normalized search finds none, defers to `findNormalized`'s
 * punctuation-stripped tier (first match only). */
export function findNthNormalized(haystack: string, needle: string, n: number): {start: number; end: number} | null {
  const norm = normalizeWhitespace(needle);
  if (norm.length === 0) return null;
  const hits: Array<{start: number; end: number}> = [];
  for (let from = 0; ;) {
    const i = haystack.indexOf(norm, from);
    if (i === -1) break;
    hits.push({start: i, end: i + norm.length});
    from = i + norm.length;
  }
  if (hits.length === 0) {
    const fallback = findNormalized(haystack, needle);
    return fallback ? {start: fallback.start, end: fallback.end} : null;
  }
  return hits[n] ?? hits[0];
}

/** Map an extract's byte range in the source `content` to a range in an
 * already-normalized rendered `haystack`. When the extract carries an `offset`,
 * its occurrence in `content` picks which match in `haystack` to take, so a
 * quote appearing many times resolves to the one the evidence cites. With no
 * `offset` or no `content` (an `Unlocated` extract) it takes the first match.
 * Returns the range in `haystack`'s normalized space; callers map it back
 * through their own char map. */
export function locate(
  content: string | null,
  extract: {text: string; offset: number | null},
  haystack: string,
): {start: number; end: number} | null {
  if (extract.offset == null || content == null) {
    const match = findNormalized(haystack, extract.text);
    return match ? {start: match.start, end: match.end} : null;
  }
  const occurrence = occurrenceInContent(content, extract.text, extract.offset);
  return findNthNormalized(haystack, extract.text, occurrence);
}

/**
 * Build a PageTextIndex from PDF.js textContent.items.
 * Each item has a `str` property. We concatenate them with spaces between,
 * normalize, and build a charMap from normalized position -> source item.
 */
export function buildPageTextIndex(pageNumber: number, items: Array<{str: string}>): PageTextIndex {
  // Concatenate raw strings, inserting a space between items — except across a
  // hyphenated line break.
  const rawChars: string[] = [];
  const rawCharMap: CharMapping[] = [];

  for (let i = 0; i < items.length; i++) {
    const str = items[i].str;
    if (i > 0) {
      // PDF.js emits a word hyphenated across a line break as two items, the
      // first ending in a hyphen ("authentica-" + "tion"). Inserting a space
      // and keeping the hyphen would leave "authentica- tion", which no
      // normalization tier rejoins — the exact tier keeps the hyphen and the
      // strip tier keeps the space — so a quote spanning the break misses.
      // When the previous item ends in a hyphen and this one continues in lower
      // case, drop the hyphen and join without a space so the word is whole.
      // A hyphen-minus (0x2D) or soft hyphen (0xAD) ending the previous item.
      const prevCode = rawChars[rawChars.length - 1]?.charCodeAt(0);
      if ((prevCode === 0x2d || prevCode === 0xad) && /^[a-z]/.test(str)) {
        rawChars.pop();
        rawCharMap.pop();
      } else {
        rawChars.push(" ");
        rawCharMap.push({itemIndex: -1, charInItem: -1}); // space between items
      }
    }
    for (let c = 0; c < str.length; c++) {
      rawChars.push(str[c]);
      rawCharMap.push({itemIndex: i, charInItem: c});
    }
  }

  // Normalize using shared function
  const {normalizedChars, mappings: charMap} = normalizeWithMapping(rawChars, rawCharMap);

  return {pageNumber, normalizedText: normalizedChars.join(""), charMap};
}

/**
 * Map a normalized character range back to per-span matches.
 */
export function mapRangeToSpanMatches(startIdx: number, endIdx: number, charMap: CharMapping[]): SpanMatch[] {
  const spanMatchMap = new Map<number, {startChar: number; endChar: number}>();

  for (let i = startIdx; i < endIdx; i++) {
    const mapping = charMap[i];
    if (mapping.itemIndex === -1) continue; // skip inter-item spaces

    const existing = spanMatchMap.get(mapping.itemIndex);
    if (existing) {
      existing.endChar = Math.max(existing.endChar, mapping.charInItem + 1);
      existing.startChar = Math.min(existing.startChar, mapping.charInItem);
    } else {
      spanMatchMap.set(mapping.itemIndex, {startChar: mapping.charInItem, endChar: mapping.charInItem + 1});
    }
  }

  const spanMatches: SpanMatch[] = [];
  for (const [itemIndex, range] of spanMatchMap) {
    spanMatches.push({itemIndex, startChar: range.startChar, endChar: range.endChar});
  }

  spanMatches.sort((a, b) => a.itemIndex - b.itemIndex);
  return spanMatches;
}

/**
 * Find a quote in a page index. Returns SpanMatch[] or null if not found.
 * Tier 1: exact normalized match. Tier 2: punctuation-stripped fallback.
 */
export function findQuoteInPageIndex(quote: string, pageIndex: PageTextIndex): ResolvedQuoteMatch | null {
  const result = findNormalized(pageIndex.normalizedText, quote, pageIndex.strippedCache);
  if (!result) return null;

  if (result.haystackCache) {
    pageIndex.strippedCache = result.haystackCache;
  }

  return {
    quoteId: "",
    pageSpans: [
      {
        pageNumber: pageIndex.pageNumber,
        spanMatches: mapRangeToSpanMatches(result.start, result.end, pageIndex.charMap),
      },
    ],
  };
}

/**
 * Find a quote that spans across two adjacent pages.
 * Returns null if the quote doesn't actually cross the boundary
 * (i.e., it would have been found by single-page search).
 */
export function findQuoteAcrossPages(
  quote: string,
  pageA: PageTextIndex,
  pageB: PageTextIndex,
): ResolvedQuoteMatch | null {
  const combined = pageA.normalizedText + " " + pageB.normalizedText;
  const result = findNormalized(combined, quote);
  if (!result) return null;

  const boundary = pageA.normalizedText.length;

  // Guard: if the match doesn't actually cross the boundary, skip it
  // (it would have been found by single-page search)
  if (result.end <= boundary || result.start >= boundary + 1) {
    return null;
  }

  // Split at the boundary - the synthetic space is at index `boundary`
  const pageAStart = result.start;
  const pageAEnd = Math.min(result.end, boundary);
  const pageBStart = Math.max(result.start, boundary + 1) - (boundary + 1);
  const pageBEnd = result.end - (boundary + 1);

  const pageSpans: PageSpan[] = [];

  if (pageAEnd > pageAStart) {
    pageSpans.push({
      pageNumber: pageA.pageNumber,
      spanMatches: mapRangeToSpanMatches(pageAStart, pageAEnd, pageA.charMap),
    });
  }

  if (pageBEnd > pageBStart) {
    pageSpans.push({
      pageNumber: pageB.pageNumber,
      spanMatches: mapRangeToSpanMatches(pageBStart, pageBEnd, pageB.charMap),
    });
  }

  if (pageSpans.length === 0) return null;

  return {quoteId: "", pageSpans};
}
/**
 * Build react-pdf's `customTextRenderer` for a page: it returns each text item's
 * string, wrapping the active quote's character range in `<mark>`.
 *
 * react-pdf assigns the return value to the text layer's `innerHTML`, so every
 * slice of the untrusted source text is HTML-escaped; the only markup this
 * produces is the `<mark>` wrapper. Character offsets index the raw string, so
 * slicing precedes escaping.
 */
export function makeCustomTextRenderer(
  activeMatch: ResolvedQuoteMatch | null,
  pageNumber: number,
): (props: {str: string; itemIndex: number}) => string {
  return ({str, itemIndex}: {str: string; itemIndex: number}) => {
    const spanMatch = activeMatch?.pageSpans
      .find(ps => ps.pageNumber === pageNumber)
      ?.spanMatches.find(m => m.itemIndex === itemIndex);
    if (!spanMatch) return escape(str);

    const before = str.slice(0, spanMatch.startChar);
    const match = str.slice(spanMatch.startChar, spanMatch.endChar);
    const after = str.slice(spanMatch.endChar);

    return `${escape(before)}<mark class="quote-highlight">${escape(match)}</mark>${escape(after)}`;
  };
}
